import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Manager, Popper, Reference } from 'react-popper';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import ErrorBoundary from '../ErrorBoundary';

const placementPropType = PropTypes.oneOf([
  'left',
  'left-start',
  'left-end',
  'right',
  'right-start',
  'right-end',
  'top',
  'top-start',
  'top-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'auto',
  'auto-start',
  'auto-end',
]);

const triggerType = PropTypes.oneOf(['click', 'hover', null]);

const propTypes = {
  arrow: PropTypes.element,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  popOverOuterClassName: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  popOverInnerStyle: PropTypes.object,
  disablePersist: PropTypes.bool,
  display: PropTypes.oneOf(['block', 'inline-block', 'inline']),
  longHoverDelay: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  modifiers: PropTypes.array,
  onOpenChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  placement: placementPropType.isRequired,
  popOver: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  popOverPortal: PropTypes.instanceOf(Element),
  preventCloseOnContentClick: PropTypes.bool,
  responsivePlacement: PropTypes.oneOf(['top', 'bottom', 'full']),
  trigger: triggerType,
};

const defaultProps = {
  arrow: null,
  popOverOuterClassName: null,
  popOverInnerStyle: undefined,
  disablePersist: false,
  display: 'block',
  longHoverDelay: false,
  modifiers: [],
  popOver: null,
  popOverPortal: null,
  preventCloseOnContentClick: false,
  responsivePlacement: undefined,
  trigger: null,
};

const ControlledPopOver = ({
  arrow,
  children,
  popOverOuterClassName,
  popOverInnerStyle,
  disablePersist,
  display,
  longHoverDelay,
  modifiers,
  onOpenChange,
  open,
  placement,
  popOver,
  popOverPortal,
  preventCloseOnContentClick,
  responsivePlacement,
  trigger: preferredTrigger,
}) => {
  const updateRef = useRef(null);
  const [popOverInner, setPopOverInner] = useState(null);
  const documentMouseOverRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const trigger =
    preferredTrigger && (isSize(Sizes.SM) ? 'click' : preferredTrigger);
  const [hasBeenOpened, setHasBeenOpened] = useState(open);

  const handleClick = () => onOpenChange(!open);

  const handleClickOutside = () => {
    if (trigger && open) {
      onOpenChange(false);
    }
  };

  const handleInsideDismissClick = (e) => {
    onOpenChange(false);
    e.stopPropagation(); // prevent handleClick gets called
  };

  const handleContentClick = (e) => {
    if (preventCloseOnContentClick) {
      e.stopPropagation();
      return;
    }
    handleClick();
  };

  const setHoverIntent = (newOpen, delay) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      onOpenChange(newOpen);
    }, delay);
  };

  const handleMouseLeave = () => setHoverIntent(false, 100);

  const handleMouseEnter = (e) => {
    if (e.buttons === 0) {
      setHoverIntent(true, longHoverDelay ? 500 : 200);

      if (e.target.disabled) {
        documentMouseOverRef.current = (event) => {
          if (
            popOverInner &&
            (popOverInner === event.target ||
              popOverInner.contains(event.target))
          ) {
            return;
          }

          handleMouseLeave(event);
        };

        // Workaround to support hover tooltip on disabled elements since mouseleave
        // is not fired on those elements.
        // See: https://github.com/facebook/react/issues/4251#issuecomment-334266778
        document.addEventListener('mouseover', documentMouseOverRef.current);
      }
    }
  };

  const handleTouchStart = () =>
    setHoverIntent(true, longHoverDelay ? 500 : 200);

  const triggerHandlers =
    {
      click: {
        onClick: handleClick,
      },
      hover: {
        onMouseEnter: handleMouseEnter,
        onTouchStart: handleTouchStart,
        onMouseLeave: handleMouseLeave,
      },
    }[trigger] || {};

  useEffect(() => {
    if (open) {
      setHasBeenOpened(true);
      updateRef.current?.();
    }
  }, [open]);

  useEffect(() => {
    // when the content within the same pop over changes, popper does not
    // get any update callbacks, so we need to monitor DOM changes and reposition
    // popper
    if (typeof MutationObserver === 'undefined' || !open || !popOverInner) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (updateRef.current) {
        updateRef.current();
      }
    });

    observer.observe(popOverInner, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [open, popOverInner]);

  useEffect(
    () => () => {
      if (documentMouseOverRef.current) {
        document.removeEventListener('mouseover', documentMouseOverRef.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    [],
  );

  const finalModifiers = useMemo(
    () => [
      {
        name: 'arrow',
        options: { padding: 6 },
      },
      {
        name: 'preventOverflow',
        options: { padding: 12 },
      },
      {
        name: 'resizeModifier',
        enabled: true,
        phase: 'main',
        fn: () => {},
        effect: ({ state, instance }) => {
          const resizeObserver = new ResizeObserver(() => instance.update());
          resizeObserver.observe(state.elements.popper);
          return () => resizeObserver.disconnect();
        },
      },
      ...modifiers,
    ],
    [modifiers],
  );

  const popper = (
    <ErrorBoundary name="popOver:popper">
      <Popper placement={placement} modifiers={finalModifiers}>
        {(popperProps) => {
          const { ref, update, style } = popperProps;
          const activePlacement = popperProps.placement || placement;
          updateRef.current = update;

          return (
            <>
              {open && responsivePlacement && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
                <div
                  data-no-print
                  className="pop-over-responsive-backdrop"
                  onClick={trigger ? handleInsideDismissClick : undefined}
                />
              )}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited */}
              <div
                className={classNames(
                  'pop-over-popper-popper-outer',
                  popOverOuterClassName,
                  {
                    // Cannot be on `pop-over` since popper could be rendered through portal
                    hidden: !open,
                    [`pop-over-responsive-${responsivePlacement}`]:
                      responsivePlacement,
                  },
                )}
                data-placement={activePlacement}
                style={style}
                onClick={trigger ? handleContentClick : undefined}
                ref={(element) => {
                  ref(element);
                }}
              >
                <div
                  className="popper-inner"
                  style={popOverInnerStyle}
                  ref={(element) => {
                    if (popOverInner !== element) {
                      setPopOverInner(element);
                    }
                  }}
                >
                  {typeof popOver === 'function'
                    ? popOver({
                        ...popperProps,
                        placement: activePlacement,
                      })
                    : popOver}
                </div>
                {arrow &&
                  React.cloneElement(arrow, {
                    'data-popper-arrow': true,
                    ...popperProps.arrowProps,
                  })}
              </div>
            </>
          );
        }}
      </Popper>
    </ErrorBoundary>
  );

  // @ts-expect-error
  // eslint-disable-next-line
  return (
    <ClickOutsideHandler
      onClickOutside={handleClickOutside}
      className={classNames('pop-over', {
        'pop-over-inline': display === 'inline',
        'pop-over-inline-block': display === 'inline-block',
      })}
      {...triggerHandlers}
    >
      <Manager>
        <Reference>
          {(referenceProps) =>
            typeof children === 'function' ? (
              children(referenceProps)
            ) : (
              <div className="pop-over-popper-target" ref={referenceProps.ref}>
                {children}
              </div>
            )
          }
        </Reference>
        {hasBeenOpened &&
          (!disablePersist || open) &&
          popOver &&
          (popOverPortal ? createPortal(popper, popOverPortal) : popper)}
      </Manager>
    </ClickOutsideHandler>
  );
};

ControlledPopOver.propTypes = propTypes;
ControlledPopOver.defaultProps = defaultProps;

const UncontrolledPopOver = (props) => {
  const [isOpen, setIsOpen] = useState(!props.trigger);
  return (
    <ControlledPopOver {...props} open={isOpen} onOpenChange={setIsOpen} />
  );
};

UncontrolledPopOver.propTypes = {
  trigger: triggerType,
};

UncontrolledPopOver.defaultProps = {
  trigger: null,
};

const PopOver = (props) => (
  <ErrorBoundary name="popOver:reference">
    {props.onOpenChange ? (
      <ControlledPopOver {...props} />
    ) : (
      <UncontrolledPopOver {...props} />
    )}
  </ErrorBoundary>
);

PopOver.propTypes = {
  onOpenChange: PropTypes.func,
};

PopOver.defaultProps = {
  onOpenChange: null,
};

export default PopOver;
